/* ==========================================================================
   Delivery feature - core
   --------------------------------------------------------------------------
   Owns: shopper location state, the location modal, countdown timers, the
   product-page widget's live bits, and the cart method selector.

   The chosen area is mirrored onto cart attributes, so Liquid renders the
   cart server-side from the same value checkout will inherit.

   Delivery fees are NOT handled here. On Plus they are real shipping rates
   filtered by a Delivery Customization Function, not a cart line item.
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG = window.JM_DELIVERY_CONFIG;
  if (!CONFIG) return;

  var STORAGE_KEY = 'jm_delivery_location';
  var RANK = { SD: 3, ND: 2, RD: 1 };

  /* ------------------------------------------------------------------ *
   * Clock. Countdowns run against server time so a wrong device clock
   * cannot invent a cutoff that has not happened.
   * ------------------------------------------------------------------ */
  var Clock = {
    driftMs: 0,

    init: function () {
      if (!CONFIG.clock || !CONFIG.clock.serverEpoch) return;
      this.driftMs = CONFIG.clock.serverEpoch * 1000 - Date.now();
    },

    // Seconds elapsed today in the store's timezone.
    secondsIntoDay: function () {
      var offsetMs = (CONFIG.clock.tzOffsetMinutes || 0) * 60000;
      var local = new Date(Date.now() + this.driftMs + offsetMs);
      return local.getUTCHours() * 3600 + local.getUTCMinutes() * 60 + local.getUTCSeconds();
    },

    secondsUntilHour: function (hour) {
      var delta = hour * 3600 - this.secondsIntoDay();
      return delta > 0 ? delta : 0;
    },

    isBefore: function (hour) {
      return this.secondsIntoDay() < hour * 3600;
    }
  };

  /* ------------------------------------------------------------------ *
   * Shopper location state
   * ------------------------------------------------------------------ */
  var State = {
    value: null,
    listeners: [],

    init: function () {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        this.value = raw ? JSON.parse(raw) : null;
      } catch (e) {
        this.value = null;
      }
    },

    get: function () {
      return this.value;
    },

    set: function (next) {
      this.value = next;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        /* private mode - state stays in memory for this page only */
      }

      this.listeners.forEach(function (fn) {
        fn(next);
      });

      CartSync.push(next).then(function () {
        if (CartSync.isCartPage()) window.location.reload();
      });
    },

    // Cart attributes are the source of truth once they exist, because they
    // are what Liquid and checkout both read. localStorage is only a
    // convenience for pages where no cart request has happened yet.
    hydrate: function () {
      var fromCart = CONFIG.cartLocation;
      var hasCart = fromCart && fromCart.area;

      if (hasCart) {
        this.value = {
          province: fromCart.province,
          area: fromCart.area,
          level: fromCart.level || null,
          source: 'cart',
          ts: Date.now()
        };
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.value));
        } catch (e) {
          /* nothing to do */
        }
        return;
      }

      // Chosen on a previous visit but not yet on this cart - push it across.
      if (this.value && this.value.area) CartSync.push(this.value);
    },

    subscribe: function (fn) {
      this.listeners.push(fn);
    },

    // "Dubai, Jumeirah Village Circle" | "Dubai" | null
    label: function () {
      if (!this.value) return null;
      var province = this.value.province;
      if (this.value.area && province) return province + ', ' + this.value.area;
      return province || this.value.area || null;
    },

    // What the chosen destination can receive.
    //   no area chosen -> null, no cap. The product metafield decides on its
    //                     own and the cart prompts for an area instead of
    //                     quietly downgrading.
    //   area chosen    -> that area's service level (blank = no cap)
    serviceLevel: function () {
      if (!this.value || !this.value.area) return null;
      return this.value.level || null;
    },

    // Nothing picked yet - drives the cart prompt.
    needsArea: function () {
      return !this.value || !this.value.area;
    }
  };

  /* ------------------------------------------------------------------ *
   * Area lookup
   *
   * The list is not in the page. Liquid caps a metaobject definition loop at
   * 50 entries and says nothing about it, so an inline list came out silently
   * truncated. It is fetched instead from the jm-areas template, which
   * paginates 250 at a time, and kept in localStorage between page views.
   *
   * Only the picker needs it, so the fetch starts when the shopper reaches
   * for the picker, not on every page load.
   * ------------------------------------------------------------------ */
  var Areas = {
    entries: [],
    loaded: false,
    promise: null,
    expected: 0,

    all: function () {
      return this.entries;
    },

    isLoaded: function () {
      return this.loaded;
    },

    // Every emirate the metaobject knows about, in the order the select shows.
    provinces: function () {
      var out = [];
      this.all().forEach(function (area) {
        if (area.province && out.indexOf(area.province) === -1) out.push(area.province);
      });
      return out.sort();
    },

    inProvince: function (province) {
      var needle = (province || '').toLowerCase();
      return this.all().filter(function (area) {
        return (area.province || '').toLowerCase() === needle;
      });
    },

    // Exact lookup. Areas are only ever chosen from the list, so there is no
    // fuzzy matching anywhere in this file by design.
    find: function (province, name) {
      var needle = (name || '').toLowerCase();
      var hit = null;
      this.inProvince(province).forEach(function (area) {
        if (!hit && (area.name || '').toLowerCase() === needle) hit = area;
      });
      return hit;
    },

    cacheKey: function () {
      return 'jm_delivery_areas_v' + (CONFIG.areasVersion || 1);
    },

    readCache: function () {
      try {
        var raw = window.localStorage.getItem(this.cacheKey());
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        var maxAgeMs = (CONFIG.areasCacheMinutes || 720) * 60000;
        if (!parsed.ts || Date.now() - parsed.ts > maxAgeMs) return null;
        if (!parsed.areas || !parsed.areas.length) return null;
        return parsed.areas;
      } catch (e) {
        return null;
      }
    },

    writeCache: function (areas) {
      try {
        // One key per version, so a bump orphans the old entry rather than
        // leaving a stale list that still looks fresh.
        window.localStorage.setItem(this.cacheKey(), JSON.stringify({ ts: Date.now(), areas: areas }));
      } catch (e) {
        /* private mode, or the quota is full - the list stays in memory */
      }
    },

    // Resolves with the full list. Safe to call repeatedly: the work happens
    // once, and a failure clears the promise so a later attempt can retry.
    load: function () {
      if (this.promise) return this.promise;

      var self = this;
      var cached = this.readCache();
      if (cached) {
        this.entries = cached;
        this.loaded = true;
        this.promise = Promise.resolve(cached);
        return this.promise;
      }

      this.promise = this.walk()
        .then(function (result) {
          var areas = result.areas;

          // A partial list is used but never kept. Caching one is what left a
          // truncated dropdown in front of shoppers for twelve hours after the
          // feed behind it was already fixed.
          if (!result.complete) {
            if (window.console) {
              window.console.warn('Delivery areas: list incomplete, not caching it. It will be fetched again on the next page.');
            }
            self.entries = areas;
            self.loaded = true;
            return areas;
          }

          // Truncation is what broke this list twice, and both times quietly.
          // A short list is legitimate when areas are marked inactive, so this
          // says what it sees rather than throwing.
          if (self.expected && areas.length < self.expected && window.console) {
            window.console.warn(
              'Delivery areas: loaded ' +
                areas.length +
                ' of ' +
                self.expected +
                ' entries. Expected if some areas are inactive; a large gap means the feed is being truncated.'
            );
          }

          self.entries = areas;
          self.loaded = true;
          self.writeCache(areas);
          return areas;
        })
        .catch(function (error) {
          self.promise = null;
          throw error;
        });

      return this.promise;
    },

    // Walks the feed by following the next link paginate emits, rather than
    // counting pages up from one. ?page=2 does nothing here: an array that is
    // not the route's own resource paginates under a generated key, and only
    // the link paginate builds for itself carries it.
    walk: function () {
      var self = this;
      var base = CONFIG.areasUrl || '/search?view=jm-areas';
      var collected = [];
      var visited = {};
      var lastPage = 0;
      var complete = false;

      function step(url) {
        if (!url || visited[url]) return Promise.resolve(collected);
        visited[url] = true;

        return self.fetchPage(url).then(function (data) {
          if (!self.expected) self.expected = data.total || 0;

          // Progress is the thing to check, not the page number we asked for.
          // A feed that ignores its pagination answers every request with page
          // one, and following its next link again would collect the same 250
          // areas forever.
          if (data.page <= lastPage) {
            if (window.console) {
              window.console.error(
                'Delivery areas: page ' +
                  data.page +
                  ' served again instead of advancing, under param ' +
                  data.pageParam +
                  '. The list is truncated.'
              );
            }
            return collected;
          }

          // The walk is only complete when the feed says there is no next
          // page. Anything else - a page that repeats, a request that fails -
          // leaves this false and the list uncached.
          complete = !data.next && data.page >= data.pages;

          lastPage = data.page;
          collected = collected.concat(data.areas || []);

          // Two ways forward, because one of them failing quietly is what put
          // a truncated list in front of shoppers twice. Normally the link
          // paginate built; failing that, the same key it would have used.
          var next = data.next;
          if (!next && data.pageParam && data.page < data.pages) {
            next = base + (base.indexOf('?') > -1 ? '&' : '?') + data.pageParam + '=' + (data.page + 1);
          }

          return next ? step(self.prepare(next)) : collected;
        });
      }

      return step(this.prepare(base)).then(function (areas) {
        // A next link that circles back would otherwise multiply the list.
        var seen = {};
        var unique = areas.filter(function (area) {
          var key = (area.province || '') + '|' + (area.name || '');
          if (seen[key]) return false;
          seen[key] = true;
          return true;
        });
        return { areas: unique, complete: complete };
      });
    },

    // paginate builds its next link from the request it was serving, so the
    // params the feed depends on should survive on their own. They are put
    // back if they ever don't: ?view= is what selects this template at all,
    // and preview_theme_id is what keeps an unpublished theme answering
    // instead of the live one, which need not have the template.
    prepare: function (url) {
      var out = url;

      var view = new URLSearchParams((CONFIG.areasUrl || '').split('?')[1] || '').get('view');
      if (view && out.indexOf('view=') === -1) {
        out += (out.indexOf('?') > -1 ? '&' : '?') + 'view=' + encodeURIComponent(view);
      }

      var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview && out.indexOf('preview_theme_id=') === -1) {
        out += (out.indexOf('?') > -1 ? '&' : '?') + 'preview_theme_id=' + encodeURIComponent(preview);
      }

      return out;
    },

    fetchPage: function (url) {
      return fetch(url, { headers: { Accept: 'text/html' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Delivery areas: HTTP ' + response.status + ' from ' + url);
          return response.text();
        })
        .then(function (body) {
          try {
            return JSON.parse(body.trim());
          } catch (e) {
            throw new Error(
              'Delivery areas: ' + url + ' did not return the feed - is templates/search.jm-areas.liquid on the live theme?'
            );
          }
        });
    }
  };

  /* ------------------------------------------------------------------ *
   * Cart attributes
   * Mirrors the chosen area onto the cart so Liquid can render badges
   * server-side and checkout inherits the selection.
   * ------------------------------------------------------------------ */
  var CartSync = {
    push: function (state) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            delivery_emirate: (state && state.province) || '',
            delivery_area: (state && state.area) || '',
            delivery_area_level: (state && state.level) || '',
            // The best tier the schedule and clock allow right now, with no
            // regard for what is in the cart. Checkout cannot work this out
            // for itself -- the weekly schedule and cutoffs are theme
            // settings, and a Function has no access to them.
            delivery_time_cap: Resolve.timeCap()
          }
        })
      });
    },

    // A cutoff passing changes the time cap even though the area has not
    // changed. Without this the cart keeps a stale cap and checkout inherits
    // a promise the clock no longer allows.
    pushTimeCap: function () {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { delivery_time_cap: Resolve.timeCap() } })
      });
    },

    // The cart page renders its badges and delivery options from those
    // attributes in Liquid, so it has to be re-rendered once they change.
    isCartPage: function () {
      return !!document.querySelector('[data-jmd-cart-page]');
    }
  };

  /* ------------------------------------------------------------------ *
   * Resolver
   * ------------------------------------------------------------------ */
  var Resolve = {
    tier: function (code) {
      var found = null;
      CONFIG.tiers.forEach(function (t) {
        if (t.code === code) found = t;
      });
      return found;
    },

    rank: function (code) {
      return RANK[code] || 0;
    },

    // A blank side imposes no cap; the other side decides alone.
    lowest: function (a, b) {
      if (!a) return b;
      if (!b) return a;
      return this.rank(a) <= this.rank(b) ? a : b;
    },

    // Does this tier run today at all? Separate from the cutoff: Sunday has
    // no same-day even at 9am.
    runsToday: function (code) {
      var av = CONFIG.availableToday;
      if (!av || !av.hasOwnProperty(code)) return true;
      return av[code] === true;
    },

    // The schedule and clock on their own, ignoring the cart and the area.
    // This is what gets mirrored to checkout as `delivery_time_cap`: the
    // fastest tier the clock still permits, which checkout then treats as a
    // ceiling it can lower but never raise.
    timeCap: function () {
      var code = 'SD';
      if (!this.runsToday('SD') || !Clock.isBefore(this.tier('SD').cutoffHour)) {
        code = 'ND';
      }
      if (code === 'ND' && (!this.runsToday('ND') || !Clock.isBefore(this.tier('ND').cutoffHour))) {
        code = 'RD';
      }
      return code;
    },

    // Three caps, applied in order:
    //   1. the destination area  - blank means "no cap, trust the product"
    //   2. the weekly schedule   - a tier that is off today is unavailable
    //   3. the clock             - past a tier's cutoff, drop to the next one
    // None can ever promote a product above what its metafield allows.
    effective: function (productType) {
      var code = this.lowest(productType, State.serviceLevel());

      if (code === 'SD' && (!this.runsToday('SD') || !Clock.isBefore(this.tier('SD').cutoffHour))) {
        code = 'ND';
      }
      if (code === 'ND' && (!this.runsToday('ND') || !Clock.isBefore(this.tier('ND').cutoffHour))) {
        code = 'RD';
      }
      return code;
    },

    feeFor: function (code, orderValue) {
      var tier = this.tier(code);
      if (!tier || !tier.fee) return 0;
      return orderValue >= tier.freeAbove ? 0 : tier.fee;
    }
  };

  /* ------------------------------------------------------------------ *
   * Formatting
   * ------------------------------------------------------------------ */
  function money(amount) {
    if (!amount) return CONFIG.copy.free;
    return CONFIG.money.prefix + amount;
  }

  function duration(seconds) {
    var h = Math.floor(seconds / 3600);
    var m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return h + ' hr' + (h === 1 ? '' : 's') + ' ' + m + ' min' + (m === 1 ? '' : 's');
    if (m > 0) return m + ' min' + (m === 1 ? '' : 's');
    return (seconds % 60) + ' sec';
  }

  // Emirate spellings differ between a Shopify address and the metaobject --
  // hyphens, capitals, spacing. Compare on letters and digits alone.
  function normalizeText(value) {
    return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /* ------------------------------------------------------------------ *
   * Countdowns. Any element with data-jmd-countdown="<hour>" ticks.
   * Elements whose tier expires re-render their owning widget.
   * ------------------------------------------------------------------ */
  var Countdown = {
    reloading: false,

    start: function () {
      this.tick();
      window.setInterval(this.tick.bind(this), 1000);
    },

    tick: function () {
      var nodes = document.querySelectorAll('[data-jmd-countdown]');
      var expired = false;

      Array.prototype.forEach.call(nodes, function (node) {
        var hour = parseInt(node.getAttribute('data-jmd-countdown'), 10);
        var left = Clock.secondsUntilHour(hour);
        if (left <= 0) {
          expired = true;
          return;
        }
        node.textContent = duration(left);
      });

      if (!expired) return;

      // The product widget re-resolves itself in place. The cart is rendered
      // by Liquid, so a cutoff passing while it sits open has to come from the
      // server - otherwise the shopper keeps an option they can no longer have.
      if (CartSync.isCartPage()) {
        if (this.reloading) return;
        this.reloading = true;
        // Refresh the cap before reloading, so the server renders against the
        // tier that is actually available now.
        var reload = function () {
          window.location.reload();
        };
        CartSync.pushTimeCap().then(reload, reload);
        return;
      }

      CartSync.pushTimeCap();
      Widgets.renderAll();
    }
  };

  /* ------------------------------------------------------------------ *
   * Header trigger
   * ------------------------------------------------------------------ */
  var Trigger = {
    init: function () {
      var self = this;
      document.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-jmd-open]');
        if (!trigger) return;
        event.preventDefault();
        Modal.open();
      });

      // Reaching for the trigger is enough of a signal to start the fetch, so
      // the list is usually there by the time the modal is. Page load is not:
      // most visits never open the picker, and the list is large.
      ['pointerover', 'focusin', 'touchstart'].forEach(function (type) {
        document.addEventListener(
          type,
          function (event) {
            if (!event.target.closest || !event.target.closest('[data-jmd-open]')) return;
            Areas.load().catch(function () {
              /* the modal reports it - a prefetch failing on its own is not
                 something the shopper needs to hear about */
            });
          },
          { passive: true }
        );
      });
      State.subscribe(function () {
        self.render();
      });
      this.render();
    },

    render: function () {
      var label = State.label() || CONFIG.copy.selectLocation;
      var nodes = document.querySelectorAll('[data-jmd-location-label]');
      Array.prototype.forEach.call(nodes, function (node) {
        node.textContent = label;
      });

      // Shown once a location exists but the area is still unknown, so the
      // shopper can lift an over-cautious cap themselves.
      var hints = document.querySelectorAll('[data-jmd-confirm-area]');
      var show = !!State.get() && State.needsArea();
      Array.prototype.forEach.call(hints, function (hint) {
        hint.hidden = !show;
      });
    }
  };

  /* ------------------------------------------------------------------ *
   * Product page widget
   * ------------------------------------------------------------------ */
  var Widgets = {
    init: function () {
      var self = this;
      State.subscribe(function () {
        self.renderAll();
      });
      this.renderAll();
    },

    renderAll: function () {
      var nodes = document.querySelectorAll('[data-jmd-widget]');
      Array.prototype.forEach.call(nodes, this.render.bind(this));
    },

    render: function (root) {
      var productType = root.getAttribute('data-jmd-widget') || 'RD';
      var effective = Resolve.effective(productType);

      // Show only the row matching the effective tier; hide the rest.
      var rows = root.querySelectorAll('[data-jmd-tier]');
      var matched = false;

      Array.prototype.forEach.call(rows, function (row) {
        var matches = row.getAttribute('data-jmd-tier') === effective;
        row.hidden = !matches;
        if (matches) matched = true;
      });

      // Name the reason same-day was lost, and only the one that applies.
      // Priority matches the cart badge: the area first, since it is permanent
      // for this address, then the clock, which clears at midnight.
      var downgraded = productType === 'SD' && effective !== 'SD';
      var areaLevel = State.serviceLevel();
      var areaCapsSd = areaLevel === 'ND' || areaLevel === 'RD';
      var pastCutoff = !Clock.isBefore(Resolve.tier('SD').cutoffHour);

      var offToday = !Resolve.runsToday('SD');

      var areaNote = root.querySelector('[data-jmd-area-note]');
      if (areaNote) areaNote.hidden = !(downgraded && areaCapsSd);

      var dayNote = root.querySelector('[data-jmd-day-note]');
      if (dayNote) dayNote.hidden = !(downgraded && !areaCapsSd && offToday);

      var cutoffNote = root.querySelector('[data-jmd-cutoff-note]');
      if (cutoffNote) cutoffNote.hidden = !(downgraded && !areaCapsSd && !offToday && pastCutoff);

      // No row for this tier - regular delivery is not rendered by default -
      // so there is nothing worth saying. Stay silent rather than half-render.
      root.hidden = !matched;
    }
  };

  /* ------------------------------------------------------------------ *
   * Location modal
   * ------------------------------------------------------------------ */
  var Modal = {
    el: null,
    lastFocus: null,
    renderList: null,
    areaInput: null,
    areasFailed: false,
    draft: { province: null, area: null, level: null, addressId: null },

    init: function () {
      this.el = document.querySelector('[data-jmd-modal]');
      if (!this.el) return;

      var self = this;

      this.el.addEventListener('click', function (event) {
        if (event.target === self.el || event.target.closest('[data-jmd-close]')) {
          self.close();
        }
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !self.el.hidden) self.close();
      });

      this.initTypeahead();
      this.initConfirm();
      this.initSavedAddresses();
    },

    // A saved address is a shortcut to the emirate, nothing more. It selects
    // the emirate below and opens the area step; the shopper still picks the
    // area from the list, so no area is ever inferred from address text.
    //
    // The whole block is absent unless the merchant enabled it and the
    // shopper has addresses, so this is inert on a page without it.
    initSavedAddresses: function () {
      var self = this;
      var saved = this.el.querySelector('[data-jmd-saved]');
      if (!saved) return;

      saved.addEventListener('click', function (event) {
        var button = event.target.closest('[data-jmd-address]');
        if (!button) return;
        event.preventDefault();

        var province = button.getAttribute('data-jmd-address-province') || '';
        var addressId = button.getAttribute('data-jmd-address') || null;
        if (!province) return;

        // The emirate list arrives with the area feed, so a click before it
        // lands has nothing to select. Waiting is the difference between the
        // shortcut working on a slow connection and silently doing nothing.
        self.loadAreas().then(function () {
          var select = self.el.querySelector('[data-jmd-province-select]');
          if (!select) return;

          var match = null;
          Array.prototype.forEach.call(select.options, function (option) {
            if (!match && normalizeText(option.value) === normalizeText(province)) {
              match = option.value;
            }
          });

          // An address in an emirate we do not cover yet. Say nothing and
          // leave the picker as it was rather than selecting something wrong.
          if (match === null) return;

          select.value = match;
          // Reuse the change handler: it clears any stale area, opens the
          // area field and shows that emirate's list. Duplicating it here is
          // how the two paths would drift apart.
          select.dispatchEvent(new Event('change', { bubbles: true }));
          self.draft.addressId = addressId;

          Array.prototype.forEach.call(saved.querySelectorAll('[data-jmd-address]'), function (other) {
            other.classList.toggle('is-selected', other === button);
          });
        });
      });
    },

    open: function () {
      if (!this.el) return;
      this.loadAreas();
      this.lastFocus = document.activeElement;
      this.el.hidden = false;
      document.body.style.overflow = 'hidden';
      var focusable = this.el.querySelector('input, button, select');
      if (focusable) focusable.focus();
    },

    close: function () {
      if (!this.el) return;
      this.el.hidden = true;
      document.body.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus();
    },

    // The picker is useless without the list, so the modal owns the fetch and
    // the two states it can end in. Called on open and on hover of a trigger;
    // Areas.load does the work once.
    loadAreas: function () {
      var self = this;

      // Not an early return. The prefetch on the trigger means the list is
      // usually already loaded by the time the modal opens, and skipping
      // straight past the fill left the emirate select sitting on "Loading
      // areas..." for every shopper whose mouse crossed the trigger first.
      if (Areas.isLoaded()) {
        this.render();
        return Promise.resolve();
      }

      return Areas.load()
        .then(function () {
          self.areasFailed = false;
          self.render();
        })
        .catch(function (error) {
          // Say so in the list rather than leaving an empty box that reads as
          // "we don't deliver anywhere".
          self.areasFailed = true;
          if (self.renderList && self.areaInput) self.renderList(self.areaInput.value);
          if (window.console) window.console.error(error);
        });
    },

    // Draws the picker from whatever the loader currently holds. Called on
    // every open, not only after a fetch, because the fetch may well have
    // happened on a previous page.
    render: function () {
      this.fillProvinces();
      if (this.renderList && this.areaInput) this.renderList(this.areaInput.value);
    },

    // Emirates come from the same feed as the areas, so the select cannot
    // offer an emirate the typeahead has no areas for.
    fillProvinces: function () {
      var select = this.el.querySelector('[data-jmd-province-select]');
      if (!select) return;

      var provinces = Areas.provinces();
      if (!provinces.length) return;

      var current = State.get();
      var preferred = (current && current.province) || this.draft.province;

      // The placeholder is a real option, not a preselected emirate. Landing
      // on Abu Dhabi because it sorts first would have shoppers searching the
      // wrong emirate's areas without ever touching this field.
      var options = ['<option value="">' + escapeHtml(CONFIG.copy.selectEmirate) + '</option>'];
      provinces.forEach(function (province) {
        options.push('<option value="' + escapeHtml(province) + '">' + escapeHtml(province) + '</option>');
      });

      select.innerHTML = options.join('');

      // Reopening lands on the emirate already chosen.
      if (preferred && provinces.indexOf(preferred) > -1) {
        select.value = preferred;
        this.draft.province = preferred;
      }
      select.disabled = false;
      this.syncAreaField();
    },

    // The area field follows the emirate. Shut and explaining itself until
    // there is one, open and searchable the moment there is.
    syncAreaField: function () {
      var input = this.el.querySelector('[data-jmd-area-input]');
      var select = this.el.querySelector('[data-jmd-province-select]');
      if (!input) return;

      var province = select ? select.value : this.draft.province;
      var ready = !!province && Areas.isLoaded();

      input.disabled = !ready;
      input.placeholder = ready
        ? input.getAttribute('data-jmd-area-placeholder') || CONFIG.copy.searchArea
        : CONFIG.copy.emirateFirst;
    },

    // An area must be pinned down before confirming. An emirate on its own
    // cannot carry a delivery promise, so allowing it would only mislead.
    syncConfirm: function () {
      var buttons = this.el.querySelectorAll('[data-jmd-confirm][data-jmd-needs-area]');
      var ready = !!(this.draft && this.draft.area);
      Array.prototype.forEach.call(buttons, function (button) {
        button.disabled = !ready;
      });
    },

    initTypeahead: function () {
      var input = this.el.querySelector('[data-jmd-area-input]');
      var list = this.el.querySelector('[data-jmd-area-list]');
      var provinceSelect = this.el.querySelector('[data-jmd-province-select]');
      if (!input || !list) return;

      var self = this;
      var activeIndex = -1;
      var matches = [];

      function areasForProvince() {
        var province = provinceSelect ? provinceSelect.value : self.draft.province;
        if (!province) return [];
        return Areas.inProvince(province)
          .slice()
          .sort(function (a, b) {
            return a.sort - b.sort || a.name.localeCompare(b.name);
          });
      }

      function render(query) {
        // Nothing to search yet. The list says which of the two it is instead
        // of showing "we don't deliver to that area yet" for every keystroke.
        if (!Areas.isLoaded()) {
          matches = [];
          var pending = self.areasFailed ? CONFIG.copy.areasError : CONFIG.copy.loadingAreas;
          list.innerHTML = '<li class="jmd-typeahead__empty">' + escapeHtml(pending) + '</li>';
          list.hidden = false;
          return;
        }

        // No emirate yet. Saying so beats "we don't deliver to that area yet",
        // which is a different problem with a different remedy.
        if (provinceSelect && !provinceSelect.value && !self.draft.province) {
          matches = [];
          list.innerHTML = '<li class="jmd-typeahead__empty">' + escapeHtml(CONFIG.copy.emirateFirst) + '</li>';
          list.hidden = false;
          return;
        }

        var needle = query.trim().toLowerCase();
        matches = areasForProvince().filter(function (area) {
          return !needle || area.name.toLowerCase().indexOf(needle) > -1;
        });

        if (!matches.length) {
          list.innerHTML = '<li class="jmd-typeahead__empty">' + escapeHtml(CONFIG.copy.noArea) + '</li>';
          list.hidden = false;
          return;
        }

        list.innerHTML = matches
          .slice(0, 40)
          .map(function (area, index) {
            var name = escapeHtml(area.name);
            if (needle) {
              var at = area.name.toLowerCase().indexOf(needle);
              name =
                escapeHtml(area.name.slice(0, at)) +
                '<mark>' +
                escapeHtml(area.name.slice(at, at + needle.length)) +
                '</mark>' +
                escapeHtml(area.name.slice(at + needle.length));
            }
            return (
              '<li class="jmd-typeahead__row' +
              (index === activeIndex ? ' is-active' : '') +
              '" data-jmd-area-option="' +
              index +
              '"><span>' +
              name +
              '</span><span class="jmd-typeahead__city">' +
              escapeHtml(area.province) +
              '</span></li>'
            );
          })
          .join('');
        list.hidden = false;
      }

      function choose(index) {
        var area = matches[index];
        if (!area) return;
        input.value = area.name;
        self.draft = {
          province: area.province,
          area: area.name,
          level: area.level || null,
          addressId: null
        };
        self.syncConfirm();
        list.hidden = true;
        activeIndex = -1;
      }

      // Held so the modal can re-render the list once the feed lands.
      self.renderList = render;
      self.areaInput = input;

      input.addEventListener('input', function () {
        activeIndex = -1;
        render(input.value);
      });

      input.addEventListener('focus', function () {
        render(input.value);
      });

      input.addEventListener('keydown', function (event) {
        if (list.hidden) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          activeIndex += event.key === 'ArrowDown' ? 1 : -1;
          if (activeIndex < 0) activeIndex = matches.length - 1;
          if (activeIndex >= matches.length) activeIndex = 0;
          render(input.value);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          choose(activeIndex > -1 ? activeIndex : 0);
        }
      });

      list.addEventListener('click', function (event) {
        var row = event.target.closest('[data-jmd-area-option]');
        if (!row) return;
        choose(parseInt(row.getAttribute('data-jmd-area-option'), 10));
      });

      if (provinceSelect) {
        provinceSelect.addEventListener('change', function () {
          input.value = '';
          // Changing province invalidates any area already picked.
          self.draft = {
            province: provinceSelect.value,
            area: null,
            level: null,
            addressId: null
          };
          self.syncConfirm();
          self.syncAreaField();

          // The emirate's areas are the answer to choosing it, so they are
          // shown straight away rather than waiting for a first keystroke.
          if (provinceSelect.value) {
            activeIndex = -1;
            render('');
            input.focus();
          } else {
            list.hidden = true;
          }
        });
      }

      document.addEventListener('click', function (event) {
        if (!event.target.closest('[data-jmd-area-field]')) list.hidden = true;
      });
    },

    initConfirm: function () {
      var self = this;
      var buttons = this.el.querySelectorAll('[data-jmd-confirm]');
      Array.prototype.forEach.call(buttons, function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          var provinceSelect = self.el.querySelector('[data-jmd-province-select]');
          if (!self.draft.province && provinceSelect) {
            self.draft.province = provinceSelect.value;
          }
          if (!self.draft.province) return;

          State.set({
            province: self.draft.province,
            area: self.draft.area,
            level: self.draft.level || null,
            addressId: self.draft.addressId,
            source: self.draft.addressId ? 'account' : 'guest',
            ts: Date.now()
          });
          self.close();
        });
      });
    }
  };

  /* ------------------------------------------------------------------ *
   * Cart method selector
   * ------------------------------------------------------------------ */
  var Method = {
    root: null,

    init: function () {
      this.root = document.querySelector('[data-jmd-method]');
      if (!this.root) return;

      var self = this;
      var options = this.root.querySelectorAll('[data-jmd-option]');

      Array.prototype.forEach.call(options, function (option) {
        option.addEventListener('change', function () {
          self.select(option.value);
        });
      });

      // Restore a previous choice within this session.
      var stored = null;
      try {
        stored = window.sessionStorage.getItem('jm_delivery_method');
      } catch (e) {
        stored = null;
      }
      if (this.isLocked()) {
        // Nothing to choose, but the summary still names the outcome.
        this.renderFeeRow('RD');
      } else if (stored) {
        this.select(stored, true);
      }

      this.gateCheckout();
    },

    select: function (code, silent) {
      var options = this.root.querySelectorAll('[data-jmd-option]');
      Array.prototype.forEach.call(options, function (option) {
        var chosen = option.value === code;
        option.checked = chosen;
        option.closest('.jmd-option').classList.toggle('is-selected', chosen);
      });

      this.root.classList.remove('is-invalid');
      var error = this.root.querySelector('[data-jmd-method-error]');
      if (error) error.hidden = true;

      try {
        window.sessionStorage.setItem('jm_delivery_method', code);
      } catch (e) {
        /* no session storage - selection lives for this page only */
      }

      this.renderFeeRow(code);

      if (!silent) {
        // TODO(wire): add / swap / remove the fee add-on line, then refresh totals.
        document.dispatchEvent(new CustomEvent('jmd:method-changed', { detail: { code: code } }));
      }
    },

    // Names the chosen method and its charge in the payment summary.
    renderFeeRow: function (code) {
      var row = document.querySelector('[data-jmd-fee-row]');
      if (!row) return;

      var tier = Resolve.tier(code);
      if (!tier) {
        row.hidden = true;
        return;
      }

      var orderValue = parseFloat(row.getAttribute('data-jmd-order-value')) || 0;
      var fee = Resolve.feeFor(code, orderValue);

      var method = row.querySelector('[data-jmd-fee-method]');
      var value = row.querySelector('[data-jmd-fee-value]');

      if (method) method.textContent = '(' + tier.label + ')';
      if (value) {
        // Regular delivery is priced by Shopify's own rates, not by an add-on.
        value.textContent = code === 'RD' ? CONFIG.copy.atCheckout : money(fee);
        value.classList.toggle('is-free', fee === 0);
      }
      row.hidden = false;
    },

    chosen: function () {
      var checked = this.root.querySelector('[data-jmd-option]:checked');
      return checked ? checked.value : null;
    },

    // A locked single-option selector (any RD in cart) needs no choice.
    isLocked: function () {
      return this.root.hasAttribute('data-jmd-locked');
    },

    // Captured on the document, not on the cart form. The theme renders three
    // checkout buttons - cart page, cart drawer and the legacy cart footer -
    // and only one of them sits inside a form. Capturing at the top catches
    // all of them and runs before the inline onclick="location.href=..."
    // the theme puts on some of them.
    gateCheckout: function () {
      var self = this;

      document.addEventListener(
        'click',
        function (event) {
          var button = event.target.closest('[name="checkout"], [data-jmd-checkout]');
          if (!button) return;

          // Only gate where the shopper can actually see the selector. From
          // the drawer there is nothing to point them at, so let it through;
          // checkout is the mandatory capture point.
          if (!self.root || self.root.offsetParent === null) return;
          if (self.isLocked() || self.chosen()) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          self.root.classList.add('is-invalid');
          var error = self.root.querySelector('[data-jmd-method-error]');
          if (error) error.hidden = false;
          self.root.scrollIntoView({ block: 'center', behavior: 'smooth' });
        },
        true
      );
    }
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot() {
    if (!CONFIG.enabled) return;
    Clock.init();
    State.init();
    State.hydrate();
    Trigger.init();
    Modal.init();
    Widgets.init();
    Method.init();
    Countdown.start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Exposed for the cart phase and for debugging in the theme editor.
  window.JMDelivery = {
    state: State,
    resolve: Resolve,
    clock: Clock,
    modal: Modal,
    money: money
  };
})();